// Angular
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
// Service
import { LayoutRenderService } from '../../../services/layout-render.service';
import { ResourceMediaControlService } from './service/resource-media-control.service';
import { SmartTagsService } from '../smart-tags/services/smart-tags.service';
import { BroadcastService, ToasterType } from '@cv-invision/core';
import { ExternalApiService, FORM_EVENT, ViewsService } from '@cv-invision/cv.invision-views.ux';
// Model
import { LayoutComponentBroadcast, LayoutRenderLayoutItem } from '../../../models/cv-layout-render.model';
import { FormModelType, ResourceMediaModel, ResourceModel } from './model/resource-media.model';
import { CaptureType } from '../panorama-tour/model/report-image.model';
import { ReportImageModel, ReportImageTargetPath } from './model/report-image.model';
import { SmartTagFormModel } from './model/smart-tag-form.model';
import { BroadcastGeneralEventMetadata, GeneralEventType, PanoramaEventType, RMCEventType } from '../../models/component.events';
import { ResourceMediaControlOptions } from '../../models/component-options/resource-media-control.options';
import { SmartTagModel } from '../smart-tags/model/smartTag.model';
// Other
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
	selector: 'cv-schema-render-resource-media-control',
	templateUrl: './resource-media-control.component.html',
	providers: [ResourceMediaControlService]
})
export class ResourceMediaControlComponent implements OnInit, OnDestroy {

	@Input() data: any;
	@Input() layoutNode = new LayoutRenderLayoutItem();

	private options = new ResourceMediaControlOptions();

	public resourceMediaModel: ResourceMediaModel = new ResourceMediaModel();
	public resourceModel: ResourceModel = new ResourceModel();

	// Capture Type
	public captureType = CaptureType;
	// Inner Component Process
	public isComponentOpen = false;
	public isSavingInProcess = false;
	public snapshotLoaded = false;
	public jsonPath = '';
	public invokedMetadata: any = null;
	public imageUrl: any = null;
	// Upload Image
	public manualUploadedImage = false;
	public acceptFormats = 'image/*';

	private atComponentDestroy = new Subject();
	private interval: any;

	get inspectionDocument(): any {
		return this.viewsService.document;
	}

	constructor(
		private viewsService: ViewsService,
		private elementRef: ElementRef,
		private broadcastService: BroadcastService,
		private resourceMediaControlService: ResourceMediaControlService,
		private layoutRenderService: LayoutRenderService,
		private smartTagServices: SmartTagsService,
		private externalApiService: ExternalApiService
	) {
	}

	ngOnInit() {
		this.options = { ...this.options, ...this.layoutNode.options };

		// Keep the Current JSON Path Context
		this.jsonPath = this.data._Path;
		this.layoutRenderService.componentBroadcast
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: LayoutComponentBroadcast) => {
				if (data.jsonPath === this.data._Path) {
					this.imageUrl = null;
					switch (data.eventType) {
						case RMCEventType.OPEN:
							this.invokedMetadata = data.metaData;
							this.openComponent();
							break;

						case RMCEventType.CLOSE:
							this.closeComponent();
							break;
					}
				}
			});

		// Listen to inner change on Components
		this.resourceMediaControlService.resourceModelChange
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((resourceModel: ResourceModel) => {
				this.resourceModel = resourceModel;
			});
	}

	/**
	 * Create the Preview Image if there is Snapshot information
	 */
	public openComponent(): void {
		// Copy the Resource Media Model
		// this.resourceMediaModel = this.invokedMetadata.resourceMediaModel;
		Object.assign(this.resourceMediaModel, this.invokedMetadata.resourceMediaModel);

		const parentElement = this.elementRef.nativeElement.closest('.resource-media-control')
		if (parentElement) {
			parentElement.classList.add('component-open');
		}

		this.resourceModel = new ResourceModel();

		if (this.resourceMediaModel.snapshot) {
			this.resourceModel.blob = this.resourceMediaModel.snapshot.blob;
			this.resourceModel.metadata = this.resourceMediaModel.snapshot.metadata;
		}

		// For Report Image is Simple
		if (this.resourceMediaModel.captureType === CaptureType.REPORT) {
			this.resourceModel.reportImage = new ReportImageModel();
		}
		// Form Smart Tag on Edit Mode copy the Model
		if (this.resourceMediaModel.captureType === CaptureType.SMARTTAG) {
			this.resourceModel.smartTagForm = new SmartTagFormModel();
			this.resourceModel.smartTagForm.imageless = this.resourceMediaModel.isImageless;
			if (this.resourceMediaModel.formModelType === FormModelType.EDIT) {
				this.resourceModel.smartTagForm.smartTagData = this.invokedMetadata.smartTagData;
			}
		}

		// Open Component
		this.isComponentOpen = true;
		// If it has a Preview, render the Preview Element
		if (this.hasSnapshotPreview()) {
			setTimeout(() => {
				const snapshotImagePreviewContainer = this.elementRef.nativeElement.querySelector('.snapshot-image-preview');
				if (this.resourceMediaModel.snapshot.renderer) {
					snapshotImagePreviewContainer.appendChild(this.resourceMediaModel.snapshot.renderer.domElement);
					this.snapshotLoaded = true;
				}

				if (this.resourceMediaModel.snapshot.url) {
					this.imageUrl = this.resourceMediaModel.snapshot.url;
				}
			}, 500);
		}
	}

	/**
	 * is Form Valid
	 */
	public isFormValid(): boolean {
		try {
			let formValid = false;

			if (this.resourceModel !== null) {
				if (this.resourceMediaModel.captureType === CaptureType.REPORT) {
					formValid =
						this.resourceModel.reportImage !== null &&
						this.resourceModel.reportImage.selectedContiguousRoom !== null &&
						this.resourceMediaModel.snapshot.preview;
				} else if (this.resourceMediaModel.captureType === CaptureType.SMARTTAG) {
					formValid =
						this.resourceModel.smartTagForm !== null &&
						this.resourceModel.smartTagForm.selectedSmartTagItem !== null &&
						this.resourceModel.smartTagForm.smartTagData !== null;

					if (!this.resourceMediaModel.isImageless) {
						formValid = formValid && this.resourceMediaModel.snapshot.preview;
					}

				}
			}

			return formValid;
		} catch (e) {
			return false;
		}
	}

	/**
	 * Validate if the Current View has Snapshot and was check as preview
	 */
	public hasSnapshotPreview(): boolean {
		return this.resourceMediaModel.snapshot !== null && this.resourceMediaModel.snapshot.preview;
	}

	/**
	 * Upload the snapshot to the Server
	 */
	public async onSave(): Promise<void> {
		this.isSavingInProcess = true;
		let mediaResponse = null;
		let message = '';
		let resourceSaveType = 'Smart Tag';
		let triggerTelemetry = false;

		if (this.resourceMediaModel.captureType === CaptureType.REPORT) {
			triggerTelemetry = true;
			mediaResponse = await this.createReportImage();
			resourceSaveType = 'Report Image';
			message = 'New Report Image Created'
		} else if (this.resourceMediaModel.captureType === CaptureType.SMARTTAG) {
			if (this.resourceMediaModel.formModelType === FormModelType.CREATE) {

				if (this.resourceMediaModel.isImageless) {
					await this.setBlankImage();
				} else {
					triggerTelemetry = true;
				}

				mediaResponse = await this.createSmartTag();
				message = 'New Smart Tag Created';
			} else if (this.resourceMediaModel.formModelType === FormModelType.EDIT) {
				mediaResponse = await this.patchSmartTag();
				message = 'Information has been updated';
			}
		}

		if (triggerTelemetry) {
			let mediaCreationType = 'ReportImage';
			if (this.resourceMediaModel.captureType === CaptureType.SMARTTAG) {
				mediaCreationType = 'SmartTag';
			}

			this.broadcastService.broadcast({
				name: FORM_EVENT.TELEMETRY,
				event: {
					telemetryEventName: `3dTour-${mediaCreationType}-SAVE`,
					data: {
						path: this.data._Path
					}
				}
			});
		}

		if (mediaResponse !== null) {

			this.broadcastService.broadcast({
				name: ToasterType.TOASTER,
				event: {
					type: 'success',
					title: resourceSaveType,
					message: message
				}
			});

			this.closeComponent();
		}

		this.isSavingInProcess = false;
	}

	/**
	 * Create a new Report Image
	 */
	public async createReportImage(): Promise<any> {
		const reportImageTargetPath: ReportImageTargetPath = await this.resourceMediaControlService.createReportImage(
			this.data,
			this.jsonPath,
			this.resourceModel.reportImage.selectedContiguousRoom,
			this.resourceMediaModel.snapshot
		);

		if (reportImageTargetPath !== null) {
			if ('jsonPath' in reportImageTargetPath && reportImageTargetPath.jsonPath !== null) {
				const broadcastMetadata = new BroadcastGeneralEventMetadata();
				broadcastMetadata.data = {
					reloadGallery: true
				};
				broadcastMetadata.isUpdate = true;

				this.layoutRenderService.componentBroadcast.next({
					eventType: GeneralEventType.UPDATE_DATA,
					jsonPath: reportImageTargetPath.jsonPath,
					metaData: broadcastMetadata
				});

				return reportImageTargetPath;
			}

			if ('error' in reportImageTargetPath) {
				this.broadcastService.broadcast({
					name: ToasterType.TOASTER,
					event: {
						type: 'danger',
						title: 'Error creating Report Image',
						message: reportImageTargetPath.error.message
					}
				});

				return null;
			}

		}

		return null
	}

	/**
	 * Create a new Smart Tag
	 */
	public async createSmartTag(): Promise<any> {
		if (this.options.groupKey && this.options.groupKey !== null) {
			this.resourceModel.smartTagForm.asGroupKey = true;
		}
		// smartTagData selectedSmartTagItem
		if (this.resourceModel.smartTagForm
			&& this.resourceModel.smartTagForm.smartTagData
			&& this.resourceModel.smartTagForm.smartTagData.description?.trim() == '') {
			this.resourceModel.smartTagForm.smartTagData.description = null;
		}
		this.resourceModel.smartTagForm.imageless = this.resourceMediaModel.isImageless;

		const newSmartTagData: SmartTagModel = await this.resourceMediaControlService.createSmartTag(
			this.data,
			this.resourceModel.smartTagForm,
			`${this.jsonPath}`,
			this.resourceMediaModel.snapshot
		);

		if (newSmartTagData !== null) {
			if ('_Path' in newSmartTagData) {
				const broadcastMetadata = new BroadcastGeneralEventMetadata();
				broadcastMetadata.data = newSmartTagData;
				// Change Path resolution for each Smart Tag Version
				const parentPath = this.smartTagServices.getParentPath(
					newSmartTagData._Path,
					(!newSmartTagData.groupName) ? 'v1' : 'v2'
				);
				broadcastMetadata.path = (!newSmartTagData.groupName) ? parentPath.path : parentPath.bucket

				broadcastMetadata.isCreate = true;

				this.layoutRenderService.componentBroadcast.next({
					eventType: GeneralEventType.UPDATE_DATA,
					jsonPath: this.jsonPath,
					section: this.data._Data.section,
					metaData: broadcastMetadata
				});

				return newSmartTagData;
			}

			if ('error' in newSmartTagData) {
				this.broadcastService.broadcast({
					name: ToasterType.TOASTER,
					event: {
						type: 'danger',
						title: 'Error creating SmartTag',
						message: newSmartTagData.error.message
					}
				});

				return null;
			}
		}
	}

	/**
	 * Patch an Smart Tag
	 */
	public async patchSmartTag(): Promise<any> {

		const newSmartTagData: SmartTagModel = await this.resourceMediaControlService.patchSmartTag(this.resourceModel);

		if (newSmartTagData && newSmartTagData !== null) {
			const broadcastMetadata = new BroadcastGeneralEventMetadata();
			broadcastMetadata.data = this.resourceModel.smartTagForm.smartTagData;
			broadcastMetadata.path = this.resourceModel.smartTagForm.smartTagData._Path;
			broadcastMetadata.isReplace = true;

			this.layoutRenderService.componentBroadcast.next({
				eventType: GeneralEventType.UPDATE_DATA,
				jsonPath: this.jsonPath,
				section: this.data._Data.section,
				metaData: broadcastMetadata
			});
		}

		return newSmartTagData;
	}

	/**
	 * Discard Process Create/Edit
	 */
	public onCancel(): void {
		if (this.resourceMediaModel.captureType === CaptureType.REPORT
			|| this.resourceMediaModel.formModelType === FormModelType.CREATE
		) {
			let mediaCreationType = 'ReportImage';
			if (this.resourceMediaModel.captureType === CaptureType.SMARTTAG) {
				mediaCreationType = 'SmartTag';
			}

			this.broadcastService.broadcast({
				name: FORM_EVENT.TELEMETRY,
				event: {
					telemetryEventName: `3dTour-${mediaCreationType}-DISCARD`,
					data: {
						path: this.data._Path
					}
				}
			});
		}
		this.closeComponent();
	}

	/**
	 * Callback after image is loaded
	 */
	onImageLoad(): void {
		this.snapshotLoaded = true;
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 * Callback for error on the image, to prevent displaying the img
	 *
	 * @param oldImageUrl
	 */
	onImageError(oldImageUrl: string): void {
		this.imageUrl = null;
		if (this.interval) {
			clearInterval(this.interval);
		}

		this.snapshotLoaded = false;
		this.interval = setInterval(() => {
			this.imageUrl = oldImageUrl;
		}, 1000);
	}

	/**
	 * Close and Emit Back to the Caller
	 * @private
	 */
	private closeComponent(): void {
		this.resourceMediaModel = new ResourceMediaModel();
		const parentElement = this.elementRef.nativeElement.closest('.resource-media-control')
		if (parentElement) {
			parentElement.classList.remove('component-open');
		}
		// has a Preview, remove it from the memory
		if (this.hasSnapshotPreview()) {
			this.elementRef.nativeElement.querySelector('.snapshot-image-preview').innerHTML = '';
		}
		this.isComponentOpen = false;
		this.snapshotLoaded = false;
		this.manualUploadedImage = false;

		this.layoutRenderService.componentBroadcast.next({
			eventType: PanoramaEventType.SAVED_SNAPSHOT,
			jsonPath: this.jsonPath
		});
	}

	/**
	 * Get the latest file Uploaded
	 * @param event
	 */
	async onFilesDropped(event: any): Promise<any> {
		// We load the image internally to show the preview
		const reader = new FileReader();
		reader.addEventListener("load", async () => {
			this.manualUploadedImage = true;
			this.resourceMediaModel.snapshot.preview = true;
			// New Image
			this.imageUrl = this.resourceMediaModel.snapshot.url = this.resourceMediaModel.snapshot.blob = reader.result;
			this.setMetadata();
		});

		reader.readAsDataURL(event[0]);
	}

	/**
	 * Clear if exist the last uploaded image
	 */
	public deleteManualUploadedImage(): void {
		this.resourceMediaModel.snapshot.preview = false;
	}

	/**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {

		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();

		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 *
	 * @returns Event to set blank image in the Imageless smart tags types
	 */
	async setBlankImage(): Promise<void> {
		const response = await this.externalApiService.getBlankImageSmartTag();
		const file = new File([response], 'placeholder-smarttags.png', { type: "image/png" });

		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.addEventListener("load", async () => {
				this.manualUploadedImage = true;
				this.resourceMediaModel.snapshot.preview = true;
				this.imageUrl = this.resourceMediaModel.snapshot.url = this.resourceMediaModel.snapshot.blob = reader.result;
				this.setMetadata();
				resolve();
			});

			reader.readAsDataURL(file);
		});
	}

	/**
	 * event to set metadata in ResourceMediaModel to send to API
	 */
	setMetadata(): void {
		// Required properties
		this.resourceMediaModel.snapshot.metadata = {
			description: (this.data.description) ? this.data.description : this.data.cv_Label,
			imageType: 'EQUIRECTANGULAR',
			isPrimary: false,
			jsonPath: this.data._Path,
			latitude: 0,
			longitude: 0,
			scanOrientation: 'equirectangular',
			timeSpan: new Date().getTime(),
			type: 'ROOM'
		};

		if (this.layoutRenderService.extraData.gpsCoords) {
			if (this.layoutRenderService.extraData.gpsCoords.cv_gpsCoordinates) {
				this.resourceMediaModel.snapshot.metadata.latitude = this.layoutRenderService.extraData.gpsCoords.cv_gpsCoordinates.latitude;
				this.resourceMediaModel.snapshot.metadata.longitude = this.layoutRenderService.extraData.gpsCoords.cv_gpsCoordinates.longitude;
			} else if (this.layoutRenderService.extraData.gpsCoords.gpsCoordinates) {
				this.resourceMediaModel.snapshot.metadata.latitude = this.layoutRenderService.extraData.gpsCoords.gpsCoordinates.latitude;
				this.resourceMediaModel.snapshot.metadata.longitude = this.layoutRenderService.extraData.gpsCoords.gpsCoordinates.longitude;
			}
		}
	}
}
